/**
 * Twilio SMS — settings card.
 *
 * Three states:
 *   1. Plan-locked (no ``sms_notifications`` flag) → upsell.
 *   2. Plan-unlocked + platform NOT configured → "available, contact
 *      admin to enable" (operator needs to set TWILIO_ACCOUNT_SID,
 *      TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER on the backend).
 *   3. Plan-unlocked + configured → green "Connected" badge with
 *      the platform's sender phone number, plus per-employee opt-in
 *      instructions.
 *
 * No connect / disconnect buttons — Twilio creds are platform-level
 * (single Twilio account on Lenavio's side), not per-org. Customers
 * opt-in by toggling the SMS flag on each employee profile.
 */
import { useEffect, useState } from "react";
import { Check, Lock, MessageCircle, Sparkles } from "lucide-react";

import { getSmsStatus, type SmsStatusResponse } from "@/api/integrationsSms";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";

export function TwilioSmsCard() {
  const { features, has, loading: featuresLoading } = usePlanFeatures();
  const planLocked = features != null && !has("sms_notifications");
  const [status, setStatus] = useState<SmsStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (planLocked || featuresLoading) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getSmsStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) setStatus({ configured: false, phone_number: null });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [planLocked, featuresLoading]);

  return (
    <Card className={planLocked ? "opacity-80" : undefined}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <MessageCircle className="h-5 w-5 text-accent" aria-hidden />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              Twilio SMS
            </h3>
            <p className="text-[11px] uppercase tracking-wider text-text-muted">
              Notifications
            </p>
          </div>
        </div>
        {planLocked ? (
          <Badge variant="default" className="uppercase tracking-wide">
            Pro
          </Badge>
        ) : status?.configured ? (
          <Badge variant="success">Connected</Badge>
        ) : (
          <Badge variant="default">Not configured</Badge>
        )}
      </div>

      {planLocked && (
        <>
          <p className="text-sm text-text-muted mb-4">
            Send SMS notifications for leave decisions, manager approvals,
            training reminders, onboarding tasks, and expense decisions —
            on top of email + Telegram.
          </p>
          <div className="rounded-lg border border-dashed border-border bg-background p-4 text-sm flex items-start gap-3">
            <Lock className="h-4 w-4 mt-0.5 text-text-muted shrink-0" aria-hidden />
            <div>
              <p className="text-text-primary font-medium">
                Upgrade to Professional
              </p>
              <p className="text-text-muted">
                SMS notifications are included on Professional and Enterprise
                plans (and during your trial).
              </p>
            </div>
          </div>
          <div className="mt-4">
            <a
              href="/settings/billing"
              className="inline-flex items-center gap-2 rounded-lg border border-accent bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent/90"
            >
              <Sparkles className="h-4 w-4" />
              View plans
            </a>
          </div>
        </>
      )}

      {!planLocked && loading && (
        <div className="h-12 rounded bg-border/30 animate-pulse" />
      )}

      {!planLocked && !loading && status?.configured && (
        <>
          <p className="text-sm mb-2 text-text-primary">
            <Check
              className="inline h-4 w-4 -mt-0.5 mr-1 text-success"
              aria-hidden
            />
            Connected — SMS sends from{" "}
            <strong className="font-mono">{status.phone_number}</strong>
          </p>
          <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs text-text-muted space-y-1.5 mb-2">
            <p className="text-text-primary font-medium">Per-employee opt-in</p>
            <p>
              Each employee chooses whether to receive SMS in{" "}
              <strong>People → [name] → Notifications</strong>. SMS only fires
              when the employee has both opted in and added a phone number.
            </p>
            <p className="pt-1">Events that fire SMS:</p>
            <ul className="ml-4 space-y-0.5">
              <li>Leave request approved / denied → submitter</li>
              <li>New leave request → manager</li>
              <li>Training due in 1 day → employee</li>
              <li>Onboarding task assigned → new hire</li>
              <li>Expense report decided → submitter</li>
            </ul>
          </div>
        </>
      )}

      {!planLocked && !loading && status && !status.configured && (
        <>
          <p className="text-sm text-text-muted mb-2">
            SMS notifications are available on your plan but the platform
            doesn't have Twilio credentials configured yet.
          </p>
          <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs text-text-muted">
            <p>
              Contact your Lenavio admin and ask them to set the{" "}
              <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>,
              and <code>TWILIO_PHONE_NUMBER</code> environment variables on
              the API service.
            </p>
          </div>
        </>
      )}
    </Card>
  );
}

export default TwilioSmsCard;
